import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { EHashLinks } from '@enum';

const checkHash = (hash: string): EHashLinks =>
  Object.values(EHashLinks)
    .map((one) => one.toString())
    .includes(hash)
    ? (hash as EHashLinks)
    : EHashLinks.home;

const setHashToSessionStorage = (hash: EHashLinks): void => {
  sessionStorage.setItem('hash', hash);
};

const getHashFromSessionStorage = (): EHashLinks => {
  const hash = sessionStorage.getItem('hash') ?? '';

  return checkHash(hash);
};

export const useChangeActiveSectionOnScroll = () => {
  const navigate = useNavigate();

  const { hash } = useLocation();

  const container = useRef<HTMLElement>();

  const [activeSection, setActiveSection] = useState<EHashLinks>(
    getHashFromSessionStorage(),
  );

  const activateScroll = (linkHash: string) => {
    const hashedSection = container?.current?.querySelector(linkHash);

    if (hashedSection)
      hashedSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const scrollToSection = useCallback((navHash: EHashLinks) => {
    activateScroll(`#${checkHash(navHash)}`);
  }, []);

  useLayoutEffect(() => {
    const handleScroll = () => {
      const scrollPosition = container?.current?.scrollTop ?? 0;
      const sections = container?.current?.querySelectorAll('section');

      sections?.forEach((section) => {
        const sectionTop = section.offsetTop;
        const sectionHeight = section.offsetHeight;

        if (
          scrollPosition >= sectionTop - 100 && // Adjust the offset as needed
          scrollPosition < sectionTop + sectionHeight - 100 // Adjust the offset as needed
        ) {
          setActiveSection(checkHash(section.id));
          setHashToSessionStorage(checkHash(section.id));
        }
      });
    };

    container?.current?.addEventListener('scroll', handleScroll);
    return () => {
      container?.current?.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useLayoutEffect(() => {
    const handleUrlAnchorChange = () => {
      const sectionFromUrl = hash.replace('#', '');
      setActiveSection(checkHash(sectionFromUrl));
      setHashToSessionStorage(checkHash(sectionFromUrl));
    };

    window.addEventListener('hashchange', handleUrlAnchorChange);
    return () => {
      window.removeEventListener('hashchange', handleUrlAnchorChange);
    };
  }, []);

  useLayoutEffect(() => {
    navigate(`#${activeSection}`);
  }, [activeSection]);

  useLayoutEffect(() => {
    if (
      Object.values(EHashLinks)
        .map((one) => one.toString())
        .includes(hash.replace('#', ''))
    ) {
      setTimeout(() => {
        activateScroll(hash);
      }, 300);
    }
  }, []);

  return { container, scrollToSection };
};
